import { useEffect, useState } from "react";

/**
 * Sizes a scroll container to the space left between its top edge and the
 * bottom of the viewport, instead of guessing the chrome above it with a
 * `calc(100vh - Npx)` that breaks whenever that chrome changes height (e.g.
 * a profile header with more meta lines). Same approach as the org chart in
 * OrganisationChartsTab.
 *
 * Returns a callback ref for the container and the height to give it
 * (`undefined` until the first measurement).
 *
 * `bottomGutter` reserves room below the container, e.g. for a pagination
 * row. Pass `remeasureKey` a value that changes when content above the
 * container changes height without resizing it (e.g. a filter chip row
 * appearing).
 */
export const useFillViewportHeight = ({
  bottomGutter = 16,
  minHeight = 300,
  remeasureKey,
}: {
  bottomGutter?: number;
  minHeight?: number;
  remeasureKey?: unknown;
} = {}) => {
  // callback ref, so the effect re-runs once the element actually mounts
  const [el, setEl] = useState<HTMLElement | null>(null);
  const [height, setHeight] = useState<number>();

  useEffect(() => {
    if (!el) return;

    const measure = () => {
      const { top } = el.getBoundingClientRect();
      // a hidden tab measures as zero; keep the last good value
      if (top <= 0) return;
      setHeight(Math.max(window.innerHeight - top - bottomGutter, minHeight));
    };

    measure();
    window.addEventListener("resize", measure);

    // width changes when the tab is switched back into view, which is exactly
    // when a re-measure is needed. Setting height cannot change width, so this
    // will not loop.
    const observer = new ResizeObserver(measure);
    observer.observe(el);

    return () => {
      window.removeEventListener("resize", measure);
      observer.disconnect();
    };
  }, [el, bottomGutter, minHeight, remeasureKey]);

  return { ref: setEl, height };
};
